import { createContext, useReducer } from 'react';
import GlobalReducer from '../reducers/globalReducer';
import { useState, useEffect } from 'react';
import {
  fetchCart,
  fetchUserInfo,
} from '../apis';
import {
  UPDATE_USER_INFO,
  UPDATE_INIT,
  SHOW_COOKIE_CONSENT,
  UPDATE_CART_COUNT,
} from '../actions/globalAction';
import { getCookie } from '../utils/common';
import { useQueries } from '@tanstack/react-query';
import useGlobalAuth from './useGlobalAuth';
import { removeTokens } from '../utils/localStorage';

const globalInitState = {
  globalError: null,
  userInfo: {},
  isInitialized: false,
  showCookieConsent: false,
  cookieConsentHeight: 0,
  cartCount: 0,
  isGreetingDialogOpen: false,
  greetingDialogLastOpen: {
    en: undefined,
    zh: undefined,
  },
};

export const GlobalContextStore = createContext(globalInitState);

const GlobalProvider = ({ children }) => {
  const { token, isGuest, isApiAccessible } = useGlobalAuth();
  const [globalState, globalDispatch] = useReducer(GlobalReducer, {
    ...globalInitState,
  });

  // Guest sessions are no longer supported, and an EXPIRED/invalid token must
  // fall back to anonymous browsing (the public site) — not a stuck loading
  // state or a forced login. Purge any guest or stale token so it can't be
  // replayed, then mark the app initialised so public pages render. Logged-in
  // customers are initialised by the userInfo query below instead.
  useEffect(() => {
    if (isGuest || (token && !isApiAccessible)) {
      removeTokens();
    }
    if (!isApiAccessible || isGuest) {
      globalDispatch({ type: UPDATE_INIT, isInitialized: true });
    }
  }, [token, isGuest, isApiAccessible]);

  const sessionValidUserQueries = useQueries({
    queries: [
      {
        queryKey: 'userInfo',
        queryFn: fetchUserInfo,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: isApiAccessible && !isGuest,
        onSuccess: (result) => {
          globalDispatch({
            type: UPDATE_USER_INFO,
            userInfo: result?.data,
            init: true,
          });
        },
        onError: () => {
          globalDispatch({ type: UPDATE_INIT, init: true });
        },
      },
      {
        queryKey: 'fetchCart',
        queryFn: fetchCart,
        enabled: isApiAccessible && !isGuest,
        onSuccess: (result) => {
          if (result?.data?.cart?.items) {
            globalDispatch({
              type: UPDATE_CART_COUNT,
              cartCount: result?.data?.cart?.items.length,
            });
          } else {
            throw new Error();
          }
        },
        onError: (err) => {
          console.log('fetch cart failed', err);
          globalDispatch({ type: UPDATE_CART_COUNT, cartCount: 0 });
        },
      },
    ],
  });

  // check cookie consent
  useEffect(() => {
    const cookieConsent = getCookie('cookieConsent');
    if (!cookieConsent) {
      globalDispatch({ type: SHOW_COOKIE_CONSENT });
    }
  }, []);

  return (
    <GlobalContextStore.Provider
      value={{ globalState, globalDispatch, isApiAccessible }}
    >
      {children}
    </GlobalContextStore.Provider>
  );
};
export default GlobalProvider;
